import { http } from '../../util';

/* APIs */
const patchUser = async ({ userId, newPassword, password, role }) => {
  return http.patch(`${import.meta.env.VITE_API2_URL}/v1/users/${userId}`, { newPassword, password, role });
};

const requestUsers = async ({ search = null, count = null, anchor = null, filters = null, sort = null }) => {
  const query = [];

  if (search) {
    query.push(`?search=${search}`);
  }

  if (count) {
    query.push(`${query.length ? '&' : '?'}count=${count}`);
  }

  if (anchor) {
    query.push(`${query.length ? '&' : '?'}anchor=${anchor}`);
  }

  if (filters) {
    Object.keys(filters).forEach((filterKey, index) => {
      let fragment;

      if (query.length) {
        fragment = index === 0 ? '&filter=' : ',';
      } else {
        fragment = '?filter=';
      }

      query.push(`${fragment}${filterKey}(${filters[filterKey]})`);
    });
  }

  if (sort) {
    Object.keys(sort).forEach((sortKey, index) => {
      let fragment;

      if (query.length) {
        fragment = index === 0 ? '&sort=' : ',';
      } else {
        fragment = '?sort=';
      }

      query.push(`${fragment}${sortKey}${sort[sortKey]}`);
    });
  }

  const { data } = await http.get(`${import.meta.env.VITE_API2_URL}/v1/users${query.join('')}`);

  const users = {
    totalResources: data.pages.totalResources,
    byId: {}
  };

  for (let i = 0; i < data.objects.length; i++) {
    const user = data.objects[i];

    users.byId[user.id] = {
      id: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phoneNumber: user.phoneNumber,
      locale: user.locale,
      country: user.country,
      createTimestamp: user.createTimestamp,
      active: user.active,
      activeOnAccount: user.activeOnAccount,
      extension: user.extension,
      timezone: user.timezone,
      lastLogin: user.lastLogin,
      role: user.role,
      accountDepartments: user.accountDepartments.map(accountDepartment => {
        return {
          id: accountDepartment.id,
          accountName: accountDepartment.accountName,
          accountNumber: accountDepartment.accountId.toString(),
          departmentNumbers: accountDepartment.departments.map(departmentNumber => {
            return departmentNumber.toString();
          })
        };
      }),
      isLockedOnAccount: user.isLockedOnAccount
    };
  }

  return users;
};

const setUserAccountDepartments = async ({ userId, accountNumber, departmentNumbers }) => {
  return http.post(`${import.meta.env.VITE_API2_URL}/v1/users/${userId}/setAccountDepartmentAccess`, {
    accountNumber,
    departmentNumbers
  });
};

const deactivateUser = async ({ userId, accountNumber }) => {
  return http.post(`${import.meta.env.VITE_API2_URL}/v1/users/${userId}/disableAccountAccess`, {
    accountNumber
  });
};

const reactivateUser = async ({ userId, accountNumber }) => {
  return http.post(`${import.meta.env.VITE_API2_URL}/v1/users/${userId}/reenableAccountAccess`, {
    accountNumber
  });
};

/* ACTIONS */
const requestRoles = {
  name: 'REQUEST_ROLES',
  action: async () => {
    const { data } = await http.get(`${import.meta.env.VITE_API2_URL}/v1/roles`);

    return {
      roles: data.objects.map(({ name }) => name).sort()
    };
  }
};

const loadInitialUsers = {
  name: 'LOAD_INITIAL_USERS',
  action: async accountNumber => {
    const [activeUsers, deactivatedUsers] = await Promise.all([
      requestUsers({ filters: { accountNumber, activeOnAccount: true } }),
      requestUsers({ filters: { accountNumber, activeOnAccount: false } })
    ]);

    return {
      counts: {
        activeUsers: activeUsers.totalResources,
        deactivatedUsers: deactivatedUsers.totalResources,
        totalUsers: activeUsers.totalResources + deactivatedUsers.totalResources
      },
      byId: activeUsers.byId,
      search: ''
    };
  }
};

const loadInitialDeactivatedUsers = {
  name: 'LOAD_INITIAL_DEACTIVATED_USERS',
  action: async accountNumber => {
    const deactivatedUsers = await requestUsers({ filters: { accountNumber, activeOnAccount: false } });

    return {
      byId: deactivatedUsers.byId,
      search: ''
    };
  }
};

const resetUsers = {
  name: 'RESET_USERS'
};

const searchSortUsers = {
  name: 'SEARCH_SORT_USERS',
  action: async ({ search, accountNumber, sort, activeOnAccount }) => {
    const filteredSort = Object.fromEntries(Object.entries(sort).filter(entry => entry[1]));

    const users = await requestUsers({ search, filters: { accountNumber, activeOnAccount }, sort: filteredSort });

    return {
      byId: users.byId,
      search,
      sort
    };
  }
};

const clearSearchInput = {
  name: 'CLEAR_SEARCH_INPUT'
};

const searchUsers = {
  name: 'SEARCH_USERS',
  action: async ({ search = null, filters = null, count = null, anchor = null, sort = null }) => {
    const filteredSort = Object.fromEntries(Object.entries(sort).filter(entry => entry[1]));

    return requestUsers({ search, filters, count, anchor, sort: filteredSort });
  }
};

const selectUser = {
  name: 'SELECT_USER',
  action: userId => userId
};

const selectUsers = {
  name: 'SELECT_USERS'
};

const selectDeactivated = {
  name: 'SELECT_DEACTIVATED'
};

const updateUser = {
  name: 'UPDATE_USER',
  action: async ({ userId, accountNumber, departmentNumbers, role, activeOnAccount }) => {
    await Promise.all([
      patchUser({ userId, role }),
      setUserAccountDepartments({ userId, accountNumber, departmentNumbers })
    ]);

    return requestUsers({ filters: { id: userId, accountNumber, activeOnAccount } });
  }
};

const toggleUserStatus = {
  name: 'TOGGLE_USER_STATUS',
  action: async ({ userId, accountNumber, isActive }) => {
    if (isActive) {
      await deactivateUser({ userId, accountNumber });
    } else {
      await reactivateUser({ userId, accountNumber });
    }
    return requestUsers({ filters: { id: userId, accountNumber } });
  }
};

// AFTER UAT, REMOVE OR UNCOMMENT BASED ON FEEDBACK
// const removeUserFromTable = {
//   name: 'REMOVE_USER_FROM_TABLE',
//   action: userId => userId
// };

const closeModal = {
  name: 'CLOSE_MODAL'
};

export const usersActions = {
  clearSearchInput,
  closeModal,
  loadInitialUsers,
  loadInitialDeactivatedUsers,
  requestRoles,
  resetUsers,
  searchSortUsers,
  searchUsers,
  selectDeactivated,
  selectUser,
  selectUsers,
  toggleUserStatus,
  updateUser
};
